import React from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, CircularProgress,
  TextField, InputAdornment, Button, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideocamIcon from '@mui/icons-material/Videocam';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import { v4 as uuidv4 } from 'uuid';
import JSZip from 'jszip';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import RequestManager from '../../utilities/requestManager';
import Store from '../../utilities/store';
import { clipState, uploadState } from '../../utilities/videoPipeline';

const PUBLIC_BUCKET = 'skyway-dev-373d5';
const PRIVATE_BUCKET = 'skyway-dev-373d5.appspot.com';

// Videos live in either bucket: the Marketing uploader writes to the private
// bucket, but `reformatVideos` saves the converted .mp4 back to the public one,
// so a path from Firestore can point at either. Try both before giving up.
const VIDEO_BUCKETS = [PRIVATE_BUCKET, PUBLIC_BUCKET];

const getVideoRef = async (filepath) => {
  for (const bucket of VIDEO_BUCKETS) {
    const ref = firebase.app().storage(bucket).ref(filepath);
    try {
      await ref.getDownloadURL();
      return ref;
    } catch (e) {
      if (e?.code === 'storage/object-not-found') continue;
      throw e;
    }
  }
  return null;
};
const CDN_BASE = 'https://cdn.skywayclassics.com/';

// cdn.skywayclassics.com and storage.googleapis.com send no CORS headers, so
// the browser blocks fetch() on our CDN URLs. The Firebase Storage API does
// send CORS headers, so fetch those images through a tokenized download URL.
// Other hosts (cdn.dealeraccelerate.com) allow CORS and are fetched directly.
const fetchImageBlob = async (url) => {
  const src = url.startsWith(CDN_BASE)
    ? await firebase.app().storage(PUBLIC_BUCKET)
        .ref(decodeURIComponent(url.slice(CDN_BASE.length).split('?')[0]))
        .getDownloadURL()
    : url;
  const res = await fetch(src);
  if (!res.ok) throw new Error(`${res.status} fetching ${url}`);
  return res.blob();
};

const IMAGE_SECTIONS = [
  { id: 'thumbnail',       label: 'Cover Photo',   multiple: false, hint: 'Main listing photo' },
  { id: 'vin_image',       label: 'VIN',            multiple: false, hint: 'VIN plate close-up' },
  { id: 'odometer_image',  label: 'Odometer',       multiple: false, hint: 'Odometer reading' },
  { id: 'stamping_images', label: 'Stampings',      multiple: true,  hint: 'Door jamb stampings' },
  { id: 'ext_images',      label: 'Exterior',       multiple: true,  hint: 'Exterior walk-around' },
  { id: 'interior_images', label: 'Interior',       multiple: true,  hint: 'Interior photos' },
  { id: 'engine_images',   label: 'Engine Bay',     multiple: true,  hint: 'Engine bay photos' },
  { id: 'under_images',    label: 'Undercarriage',  multiple: true,  hint: 'Undercarriage photos' },
];

const VIDEO_TYPES = [
  { id: 'walkaround_video',   label: 'Walk Around' },
  { id: 'engine_video',       label: 'Engine Bay' },
  { id: 'convertible_video',  label: 'Convertible Top' },
  { id: 'driving_video',      label: 'Driving' },
  { id: 'undercarriage_video',label: 'Undercarriage' },
];

const getExt = filename => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : 'jpg';
};

// iPhone photos are frequently HEIC/HEIF, which canvas (and therefore
// browser-image-compression) can't decode and most browsers can't render.
// Convert them to JPEG in the browser before compressing/uploading.
const isHeic = file =>
  /image\/hei[cf]/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');

const convertHeic = async file => {
  if (!isHeic(file)) return file;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const jpeg = Array.isArray(blob) ? blob[0] : blob;
  const name = (file.name || 'image').replace(/\.hei[cf]$/i, '.jpg');
  return new File([jpeg], /\.jpe?g$/i.test(name) ? name : `${name}.jpg`, { type: 'image/jpeg' });
};

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
export default function Marketing({ car = {} }) {
  const theme = useTheme();

  const getImages = (id, multiple) => {
    const raw = car[id];
    if (!raw) return [];
    return multiple ? (Array.isArray(raw) ? raw.filter(Boolean) : [raw]) : [raw];
  };

  const completedCount = IMAGE_SECTIONS.filter(s => getImages(s.id, s.multiple).length > 0).length;

  // null when idle, else { done, total } so the button can show progress.
  const [download, setDownload] = React.useState(null);

  const downloadAll = async () => {
    if (download) return;
    const files = IMAGE_SECTIONS.flatMap(({ id }) =>
      getImages(id, true).map((url, i) => ({
        url,
        name: `${id}_${i}.${getExt(url.split('?')[0].split('/').pop())}`,
      }))
    );
    if (!files.length) {
      StateManager.setAlertAndOpen('No images to download', 'warning');
      return;
    }
    const zip = new JSZip();
    let done = 0;
    setDownload({ done, total: files.length });
    // Six workers pull from one queue so a 40+ photo car doesn't open every
    // request at once.
    const queue = [...files];
    const worker = async () => {
      while (queue.length) {
        const { url, name } = queue.shift();
        zip.file(name, await fetchImageBlob(url));
        done += 1;
        setDownload({ done, total: files.length });
      }
    };
    try {
      await Promise.all(Array.from({ length: 6 }, worker));
      const content = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = 'car_images.zip';
      a.click();
      // Revoking in the same tick can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      StateManager.setAlertAndOpen('Download complete!', 'success');
    } catch (e) {
      console.error(e);
      StateManager.setAlertAndOpen('Error creating zip file', 'error');
    } finally {
      setDownload(null);
    }
  };

  return (
    // Negative margins cancel the parent Box's p:2 so we can control layout fully
    <Box sx={{ mx: -2, mt: -2 }}>

      {/* ── Completion header (sticky) ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: { xs: 2, sm: 3 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5, whiteSpace: 'nowrap' }}
        >
          {completedCount}/{IMAGE_SECTIONS.length}
        </Typography>

        {IMAGE_SECTIONS.map(s => {
          const done = getImages(s.id, s.multiple).length > 0;
          return (
            <Chip
              key={s.id}
              label={s.label}
              size="small"
              color={done ? 'success' : 'default'}
              variant={done ? 'filled' : 'outlined'}
              icon={done
                ? <CheckCircleIcon style={{ fontSize: 13 }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 13 }} />
              }
              onClick={() => document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              data-sold-ok
              sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 24 }}
            />
          );
        })}

        <Box sx={{ flexGrow: 1 }} />

        {download ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress
              size={18}
              variant={download.done < download.total ? 'determinate' : 'indeterminate'}
              value={(download.done / download.total) * 100}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
              {download.done < download.total
                ? `Downloading ${download.done}/${download.total}`
                : 'Zipping…'}
            </Typography>
          </Box>
        ) : (
          <Tooltip title="Download all images as ZIP">
            <IconButton size="small" onClick={downloadAll} data-sold-ok sx={{ color: theme.palette.text.secondary }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Section cards ── */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {IMAGE_SECTIONS.map(section => (
          <ImageSectionCard key={section.id} section={section} car={car} />
        ))}
        <VideoSectionCard car={car} />
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Image section card
// ─────────────────────────────────────────────
function ImageSectionCard({ section, car }) {
  const theme = useTheme();
  const { id, label, multiple, hint } = section;

  const init = () => {
    let d = car[id];
    if (!Array.isArray(d)) d = [d].filter(Boolean);
    return d;
  };

  const [pics, setPics] = React.useState(init);
  const [uploading, setUploading] = React.useState(false);
  const [pending, setPending] = React.useState(0);
  const dragItem = React.useRef();
  const dragOverItem = React.useRef();

  const uploadFiles = async (files) => {
    setUploading(true);
    setPending(files.length);
    const options = { maxSizeMB: 1, maxWidthOrHeight: 1920 };
    const locations = [];
    try {
      for (const original of files) {
        try {
          let file = original;
          try {
            file = await convertHeic(original);
          } catch (err) {
            StateManager.setAlertAndOpen(`Could not convert ${original.name} from HEIC`, 'error');
            continue;
          }
          let compressed = file;
          try { compressed = await imageCompression(file, options); } catch {}
          const filename = `images/${uuidv4()}.${getExt(file.name)}`;
          const ref = firebase.app().storage(PUBLIC_BUCKET).ref(filename);
          await ref.put(compressed);
          locations.push(CDN_BASE + filename);
        } finally {
          // Retire this tile's placeholder as soon as the file is done (or failed).
          setPending(c => Math.max(0, c - 1));
        }
      }
      if (!locations.length) return;
      const update = multiple
        ? { [id]: firebase.firestore.FieldValue.arrayUnion(...locations), picsRefreshed: true }
        : { [id]: locations[0], picsRefreshed: true };
      await firebase.firestore().doc('cars/' + car.stock).set(update, { merge: true });
      const newPics = multiple ? [...pics, ...locations] : [locations[0]];
      setPics(newPics);
      StateManager.updateCar({ [id]: multiple ? newPics : locations[0] });
      StateManager.setAlertAndOpen('Uploaded!', 'success');
    } catch (err) {
      StateManager.setAlertAndOpen('Upload failed', 'error');
    } finally {
      setPending(0);
      setUploading(false);
    }
  };

  const deleteImage = async (index) => {
    if (!window.confirm('Delete this image?')) return;
    StateManager.setLoading(true);
    const newPics = multiple ? pics.filter((_, i) => i !== index) : null;
    await firebase.firestore().doc('cars/' + car.stock).set({ [id]: newPics, picsRefreshed: true }, { merge: true });
    setPics(multiple ? newPics : []);
    StateManager.updateCar({ [id]: newPics });
    StateManager.setLoading(false);
  };

  const dragStart = (_, pos) => { dragItem.current = pos; };
  const dragEnter = (_, pos) => { dragOverItem.current = pos; };
  const drop = () => {
    const copy = [...pics];
    const moved = copy[dragItem.current];
    copy.splice(dragItem.current, 1);
    copy.splice(dragOverItem.current, 0, moved);
    dragItem.current = null;
    dragOverItem.current = null;
    setPics(copy);
    StateManager.updateCar({ [id]: copy });
    firebase.firestore().doc('cars/' + car.stock).set({ [id]: copy, picsRefreshed: true }, { merge: true });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    accept: 'image/*,.heic,.heif',
    onDrop: uploadFiles,
  });

  const hasPics = pics.length > 0;
  const showEmpty = !hasPics && !pending;

  // Placeholder shown in the grid/slot for each file still uploading.
  const PendingTile = ({ big }) => (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        aspectRatio: big ? undefined : '4/3',
        height: big ? { xs: 180, sm: 260 } : undefined,
        boxShadow: theme.shadows[1],
        bgcolor: theme.palette.grey[100],
        border: `1px dashed ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <CircularProgress size={big ? 28 : 22} />
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Uploading…</Typography>
    </Box>
  );

  return (
    <Box
      id={`section-${id}`}
      sx={{
        border: `1px solid ${hasPics ? theme.palette.success.light : theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          gap: 1,
          bgcolor: hasPics ? alpha(theme.palette.success.main, 0.04) : alpha(theme.palette.grey[100], 0.6),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {hasPics
          ? <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main, flexShrink: 0 }} />
          : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: theme.palette.text.disabled, flexShrink: 0 }} />
        }
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>{label}</Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: { xs: 'none', sm: 'block' } }}>
            {hint}
          </Typography>
        )}
        {hasPics && (
          <Chip
            label={`${pics.length} photo${pics.length !== 1 ? 's' : ''}`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {uploading && <CircularProgress size={16} />}
      </Box>

      {/* Body */}
      {showEmpty ? (
        // Empty state — large drop zone
        <Box
          {...getRootProps()}
          sx={{
            m: 2,
            p: { xs: 4, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            borderRadius: 1.5,
            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
            bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
            transition: 'all 0.15s',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 36, color: isDragActive ? theme.palette.primary.main : theme.palette.text.disabled }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
            {isDragActive ? 'Drop photos here' : 'Drag photos here or click to select'}
          </Typography>
          {!multiple && (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>Single photo</Typography>
          )}
        </Box>
      ) : multiple ? (
        // Multi-image grid
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fill, minmax(155px, 1fr))' },
              gap: 1.5,
            }}
          >
            {pics.map((image, index) => (
              <Box
                key={index}
                draggable
                onDragStart={e => dragStart(e, index)}
                onDragEnter={e => dragEnter(e, index)}
                onDragEnd={drop}
                sx={{
                  position: 'relative',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                  boxShadow: theme.shadows[1],
                  bgcolor: theme.palette.grey[100],
                  cursor: 'grab',
                  '&:hover .img-actions': { opacity: 1 },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt=""
                  onClick={() => window.open(image, '_blank')}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                />
                <Box
                  className="img-actions"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    transition: 'opacity 0.15s',
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 40%)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    p: 0.75,
                    pointerEvents: 'none',
                  }}
                >
                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.4)', borderRadius: 0.75, p: 0.25, display: 'flex' }}>
                    <DragIndicatorIcon sx={{ fontSize: 15, color: 'white' }} />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); deleteImage(index); }}
                    sx={{ pointerEvents: 'auto', bgcolor: 'rgba(0,0,0,0.4)', color: 'white', p: 0.4, '&:hover': { bgcolor: 'rgba(211,47,47,0.8)' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}

            {/* Uploading placeholders */}
            {Array.from({ length: pending }).map((_, i) => (
              <PendingTile key={`pending-${i}`} />
            ))}

            {/* Add more cell */}
            <Box
              {...getRootProps()}
              sx={{
                aspectRatio: '4/3',
                borderRadius: 1.5,
                border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                gap: 0.5,
                bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: theme.palette.primary.light, bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              <input {...getInputProps()} />
              <AddPhotoAlternateIcon sx={{ fontSize: 22, color: isDragActive ? theme.palette.primary.main : theme.palette.text.disabled }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>Add more</Typography>
            </Box>
          </Box>
        </Box>
      ) : pending > 0 ? (
        // Single image mid-upload — placeholder
        <Box sx={{ p: 2 }}>
          <PendingTile big />
        </Box>
      ) : (
        // Single image — show full-width with overlay controls
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 1.5,
              overflow: 'hidden',
              maxHeight: { xs: 180, sm: 260 },
              boxShadow: theme.shadows[1],
              bgcolor: theme.palette.grey[100],
              display: 'flex',
              '&:hover .img-actions': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={pics[0]}
              alt=""
              onClick={() => window.open(pics[0], '_blank')}
              sx={{ width: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
            />
            <Box
              className="img-actions"
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                transition: 'opacity 0.15s',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 35%, transparent 65%, rgba(0,0,0,0.3) 100%)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                p: 1,
                pointerEvents: 'none',
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); deleteImage(0); }}
                sx={{ pointerEvents: 'auto', bgcolor: 'rgba(0,0,0,0.45)', color: 'white', '&:hover': { bgcolor: 'rgba(211,47,47,0.8)' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Replace drop zone overlay at bottom */}
            <Box
              {...getRootProps()}
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
              }}
              onClick={e => e.stopPropagation()}
            >
              <input {...getInputProps()} />
              <Button
                size="small"
                variant="contained"
                startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem', py: 0.4, px: 1.25, bgcolor: 'rgba(0,0,0,0.55)', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' }, boxShadow: 'none' }}
              >
                Replace
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────
// Video section card
// ─────────────────────────────────────────────
const REQUIRED_VIDEO_IDS = ['walkaround_video', 'driving_video', 'undercarriage_video'];

function VideoSectionCard({ car }) {
  const theme = useTheme();
  const [ytLink, setYtLink] = React.useState(car.youtube_link || '');
  const [merging, setMerging] = React.useState(false);

  React.useEffect(() => {
    setYtLink(car.youtube_link || '');
  }, [car.youtube_link]);

  // Readiness checks — required videos must exist and be mp4 (not still converting from .mov)
  const requiredStatus = REQUIRED_VIDEO_IDS.map(id => {
    const path = car[id];
    if (!path) return { id, state: 'missing' };
    if (!path.toLowerCase().endsWith('.mp4')) {
      return { id, state: clipState(car, id)?.state === 'converting' ? 'converting' : 'stuck' };
    }
    return { id, state: 'ready' };
  });
  const labelFor = (s) => VIDEO_TYPES.find(v => v.id === s.id)?.label || s.id;
  const allReady = requiredStatus.every(s => s.state === 'ready');
  const anyConverting = requiredStatus.some(s => s.state === 'converting');
  const stuckRequired = requiredStatus.filter(s => s.state === 'stuck').map(labelFor);
  const missingRequired = requiredStatus.filter(s => s.state === 'missing').map(labelFor);
  const upload = uploadState(car);

  const handleRetryUpload = () => {
    StateManager.updateCar({ video_upload: { ...car.video_upload, next_attempt_at: null } });
    Store.retryVideoUpload(car.stock).catch(() => {
      StateManager.setAlertAndOpen('Could not queue the upload retry. Try again.', 'error');
    });
  };

  const saveYtLink = async (value) => {
    if (value === (car.youtube_link || '')) return;
    StateManager.updateCar({ youtube_link: value });
    await firebase.firestore().doc('cars/' + car.stock).set({ youtube_link: value, needsDAUpdate: true }, { merge: true });
    StateManager.setAlertAndOpen('YouTube link saved!', 'success');
  };

  const handleMergeAndPublish = async () => {
    if (merging || !allReady) return;
    if (ytLink && !window.confirm('A YouTube link already exists. Re-merge and overwrite it?')) return;
    setMerging(true);
    StateManager.setAlertAndOpen('Merging and uploading to YouTube — this takes 10–20 minutes. You can keep working.', 'info');
    try {
      const response = await RequestManager.post({ function: 'mergeVideos', variables: { stock: car.stock } });
      if (response?.video) {
        await firebase.firestore().doc('cars/' + car.stock).set({ youtube_link: response.video, needsDAUpdate: true }, { merge: true });
        setYtLink(response.video);
        StateManager.updateCar({ youtube_link: response.video });
        StateManager.setAlertAndOpen('Videos merged & published to YouTube!', 'success');
      } else {
        throw new Error(response?.error || 'No video URL returned');
      }
    } catch (err) {
      const msg = err?.message || 'Merge failed';
      StateManager.setAlertAndOpen(msg, 'error');
    } finally {
      setMerging(false);
    }
  };

  const uploadedCount = VIDEO_TYPES.filter(v => !!car[v.id]).length;
  const hasYt = !!ytLink;

  let mergeTooltip = '';
  if (!allReady) {
    if (stuckRequired.length) mergeTooltip = `Conversion is stuck for: ${stuckRequired.join(', ')}. See the video rows above.`;
    else if (anyConverting) mergeTooltip = 'Some required videos are still being converted to mp4. Check back in a few minutes.';
    else mergeTooltip = `Missing required videos: ${missingRequired.join(', ')}`;
  }

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          gap: 1,
          bgcolor: alpha(theme.palette.grey[100], 0.6),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <VideocamIcon sx={{ fontSize: 16, color: uploadedCount > 0 ? theme.palette.success.main : theme.palette.text.disabled, flexShrink: 0 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>Videos</Typography>
        {uploadedCount > 0 && (
          <Chip
            label={`${uploadedCount}/${VIDEO_TYPES.length} uploaded`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Video rows */}
      {VIDEO_TYPES.map((v, i) => (
        <VideoRow
          key={v.id}
          id={v.id}
          label={v.label}
          required={REQUIRED_VIDEO_IDS.includes(v.id)}
          car={car}
          divider={i < VIDEO_TYPES.length - 1}
        />
      ))}

      {/* YouTube link + merge */}
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
        }}
      >
        {/* Readiness warning */}
        {!allReady && (
          <Typography variant="caption" color={stuckRequired.length ? 'error.main' : anyConverting ? 'warning.dark' : 'text.secondary'}>
            {stuckRequired.length
              ? `Conversion is stuck for: ${stuckRequired.join(', ')}. See the video rows above.`
              : anyConverting
                ? 'Required videos are still being converted to mp4. Check back in a few minutes.'
                : `Required videos missing: ${missingRequired.join(', ')}.`
            }
          </Typography>
        )}

        {upload && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <ErrorOutlineIcon sx={{ fontSize: 16, color: theme.palette.error.main, mt: 0.25 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="error.main" display="block">{upload.text}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                {upload.error}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={handleRetryUpload}>Retry now</Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            label="YouTube Link"
            value={ytLink}
            onChange={e => setYtLink(e.target.value)}
            onBlur={e => saveYtLink(e.target.value)}
            size="small"
            placeholder="https://youtube.com/watch?v=…"
            sx={{ flexGrow: 1, minWidth: 220 }}
            InputProps={hasYt ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Open YouTube link">
                    <IconButton size="small" onClick={() => window.open(ytLink, '_blank')} data-sold-ok>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            } : undefined}
          />
          <Tooltip title={mergeTooltip} disableHoverListener={allReady}>
            <span>
              <Button
                variant="contained"
                onClick={handleMergeAndPublish}
                disabled={merging || !allReady}
                startIcon={merging ? <CircularProgress size={14} color="inherit" /> : <VideocamIcon />}
              >
                {merging ? 'Processing…' : 'Merge & Publish'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Individual video row
// ─────────────────────────────────────────────
function VideoRow({ id, label, required, car, divider }) {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);
  const [uploaded, setUploaded] = React.useState(!!car[id]);
  const [filepath, setFilepath] = React.useState(car[id] || null);

  const isConverting = filepath && !filepath.toLowerCase().endsWith('.mp4');
  const clip = clipState(car, id);
  const clipStuck = clip && clip.state !== 'converting';

  // A new file starts the clip over, so drop the old failure state with it
  const clearClipState = () => {
    const attempts = { ...(car.video_reformat_attempts || {}) };
    delete attempts[id];
    StateManager.updateCar({ video_reformat_attempts: attempts, [`${id}_status`]: null });
    return Store.retryVideoClip(car.stock, id);
  };

  const handleRetry = () => {
    clearClipState().catch(() => {
      StateManager.setAlertAndOpen('Could not queue the conversion retry. Try again.', 'error');
    });
  };

  React.useEffect(() => {
    setUploaded(!!car[id]);
    setFilepath(car[id] || null);
  }, [car, id]);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: 'video/*',
    onDrop: async ([file]) => {
      if (!file) return;
      setLoading(true);
      const filename = `videos/${car.stock || uuidv4()}-${id}.${getExt(file.name)}`;
      const storageRef = firebase.app().storage(PRIVATE_BUCKET).ref(filename);
      StateManager.setAlertAndOpen('Upload started! You can navigate away.', 'info');
      StateManager.uploadFile({ storageRef, file }).then(async result => {
        if (result) {
          setUploaded(true);
          setFilepath(filename);
          StateManager.updateCar({ [id]: filename });
          await firebase.firestore().doc('cars/' + car.stock).set({ [id]: filename }, { merge: true });
          if (car[`${id}_status`] || (car.video_reformat_attempts || {})[id]) {
            clearClipState().catch(() => {
              StateManager.setAlertAndOpen('Video uploaded, but its old error could not be cleared.', 'error');
            });
          }
        }
        setLoading(false);
      });
    },
  });

  const handleView = async () => {
    if (!filepath) return;
    setLoading(true);
    try {
      const ref = await getVideoRef(filepath);
      if (!ref) {
        StateManager.setAlertAndOpen('That video file is missing from storage.', 'error');
        return;
      }
      window.open(await ref.getDownloadURL(), '_blank');
    } catch (e) {
      StateManager.setAlertAndOpen('Could not open that video.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this video?')) return;
    setLoading(true);
    try {
      const ref = await getVideoRef(car[id]);
      if (ref) await ref.delete();
    } catch {}
    await firebase.firestore().doc('cars/' + car.stock).set({ [id]: null }, { merge: true });
    setUploaded(false);
    setFilepath(null);
    StateManager.updateCar({ [id]: null });
    setLoading(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 2,
        gap: 2,
        borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none',
      }}
    >
      {loading
        ? <CircularProgress size={16} sx={{ flexShrink: 0 }} />
        : uploaded && !isConverting
          ? <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main, flexShrink: 0 }} />
          : clipStuck
            ? <ErrorOutlineIcon sx={{ fontSize: 16, color: theme.palette.error.main, flexShrink: 0 }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: theme.palette.text.disabled, flexShrink: 0 }} />
      }
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
          {required && <Typography component="span" variant="caption" sx={{ ml: 0.75, color: theme.palette.text.disabled }}>required</Typography>}
        </Typography>
        {isConverting && clip && (
          <Typography variant="caption" color={clip.color} display="block">{clip.text}</Typography>
        )}
        {isConverting && clip && clip.error && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
            {clip.error}
          </Typography>
        )}
      </Box>
      {uploaded ? (
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {clip && clip.retryable && (
            <Button size="small" variant="outlined" onClick={handleRetry} disabled={loading}>Retry now</Button>
          )}
          <Button size="small" variant="outlined" onClick={handleView} disabled={loading} data-sold-ok>View</Button>
          <Button size="small" variant="outlined" color="error" onClick={handleDelete} disabled={loading}>Remove</Button>
        </Box>
      ) : (
        <Box {...getRootProps()} sx={{ flexShrink: 0 }}>
          <input {...getInputProps()} />
          <Button size="small" variant="contained" disabled={loading}>Upload</Button>
        </Box>
      )}
    </Box>
  );
}
